import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Loader2, Download, RefreshCw, Copy, Check, ExternalLink, Sparkles,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * AdminMonogramLibrary
 *
 * Preview + download every approved M&A monogram variant (Gold on Black,
 * Gold on Ivory, Solid Black, Solid White, and the QR-optimized version).
 * SVG is the master vector file for print; PNG is a 2048 px transparent
 * bitmap for quick digital reuse. Each row exposes copyable public URLs so
 * stationery designers and vendors can pull the same artwork without an
 * admin login when the couple chooses to share the link.
 *
 * The variants are generated server-side from real Cormorant Garamond
 * outlines + an italic calligraphic ampersand (see /app/backend/monogram_builder.py),
 * so what admins see here is byte-identical to what ships on invitations,
 * signage, and inside the QR badge.
 */
const AdminMonogramLibrary = ({ authHeader }) => {
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rebuilding, setRebuilding] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(''); // "<variant>:<kind>" key
  const [downloading, setDownloading] = useState(''); // "<variant>:<kind>"

  const fetchCatalog = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/monogram/list`);
      // Backend returns relative API paths so we can prefix them with the
      // frontend's configured backend URL (avoids leaking internal cluster
      // hostnames through the preview ingress).
      const enriched = (res.data?.variants || []).map((v) => ({
        ...v,
        svgUrl: `${BACKEND_URL}${v.svgPath}`,
        pngUrl: `${BACKEND_URL}${v.pngPath}`,
      }));
      setVariants(enriched);
    } catch (err) {
      setError('Could not load the monogram catalog. Please refresh.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCatalog(); }, [fetchCatalog]);

  const rebuild = async () => {
    setRebuilding(true);
    try {
      await axios.post(`${API}/admin/monogram/rebuild`, null, { headers: authHeader() });
      await fetchCatalog();
    } catch (err) {
      setError('Rebuild failed. Please check the backend logs.');
    } finally {
      setRebuilding(false);
    }
  };

  const copyLink = async (variantName, kind, url) => {
    try {
      await navigator.clipboard.writeText(url);
      const key = `${variantName}:${kind}`;
      setCopied(key);
      setTimeout(() => setCopied((c) => (c === key ? '' : c)), 1400);
    } catch { /* clipboard may be blocked; downloadable link remains */ }
  };

  const download = async (variantName, kind, url) => {
    const key = `${variantName}:${kind}`;
    setDownloading(key);
    try {
      const res = await axios.get(url, { responseType: 'blob' });
      const mime = kind === 'svg' ? 'image/svg+xml' : 'image/png';
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `mary-arrington-monogram-${variantName}.${kind}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 4000);
    } finally {
      setDownloading('');
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-10 flex items-center justify-center gap-3 text-[#5A4A4A]">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading monogram library…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-monogram-panel">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 text-[#C8A951] text-xs uppercase tracking-[0.2em] mb-1">
            <Sparkles className="w-3.5 h-3.5" /> Brand mark
          </div>
          <h2 className="font-serif text-2xl text-[#3B2F2F]">M&amp;A Monogram Library</h2>
          <p className="text-sm text-[#5A4A4A] max-w-2xl mt-1">
            Production-ready vector monograms for the invitation suite, signage, packaging, and website.
            Refined from the approved reference — Cormorant Garamond serif capitals with a hand-tuned
            italic calligraphic ampersand.
          </p>
        </div>
        <button
          onClick={rebuild}
          disabled={rebuilding}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#3B2F2F] text-white text-sm rounded-full hover:bg-[#2A2020] disabled:opacity-60"
          data-testid="admin-monogram-rebuild"
        >
          {rebuilding ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Rebuild all
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">
          {error}
        </div>
      )}

      {/* Variant cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="admin-monogram-variants">
        {variants.map((v) => {
          // Choose a preview surface that showcases the intended pairing:
          //  - gold-on-black → dark sable card
          //  - solid-white   → dark sable card so the mark is visible
          //  - qr-optimized  → circular white "badge" preview
          //  - everything else → ivory card
          const isDarkSurface = v.name === 'gold-on-black' || v.name === 'solid-white';
          const isBadge = v.name === 'qr-optimized';
          return (
            <div
              key={v.name}
              className="bg-white rounded-xl shadow-sm overflow-hidden"
              data-testid={`admin-monogram-card-${v.name}`}
            >
              {/* Preview surface */}
              <div
                className={`p-8 flex items-center justify-center min-h-[220px] ${
                  isDarkSurface ? 'bg-[#1A1512]' : 'bg-[#F8F5EF]'
                }`}
              >
                {isBadge ? (
                  <div
                    className="w-40 h-40 rounded-full bg-white shadow-inner flex items-center justify-center border-2 border-[#C8A951]"
                    title="QR center badge — actual proportion"
                  >
                    <img
                      src={`${v.svgUrl}?t=${Date.now()}`}
                      alt={`${v.label} preview`}
                      className="w-[68%] h-[68%] object-contain"
                    />
                  </div>
                ) : (
                  <img
                    src={`${v.svgUrl}?t=${Date.now()}`}
                    alt={`${v.label} preview`}
                    className="max-h-[160px] w-auto object-contain"
                  />
                )}
              </div>

              {/* Meta + actions */}
              <div className="p-5">
                <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                  <div className="font-serif text-lg text-[#3B2F2F]">{v.label}</div>
                  <div className="flex items-center gap-2 text-xs text-[#5A4A4A]">
                    <span
                      className="inline-block w-3 h-3 rounded-full border border-black/10"
                      style={{ background: v.fill }}
                      aria-hidden="true"
                    />
                    <code className="font-mono">{v.fill}</code>
                  </div>
                </div>
                <p className="text-xs text-[#7A6A6A] mb-4">{v.hint}</p>

                <div className="flex flex-wrap gap-2 mb-3">
                  <button
                    onClick={() => download(v.name, 'svg', v.svgUrl)}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#C8A951] text-white text-xs hover:bg-[#B09240] disabled:opacity-60"
                    disabled={downloading === `${v.name}:svg`}
                    data-testid={`admin-monogram-download-svg-${v.name}`}
                  >
                    {downloading === `${v.name}:svg`
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <Download className="w-3.5 h-3.5" />}
                    SVG (master)
                  </button>
                  <button
                    onClick={() => download(v.name, 'png', v.pngUrl)}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#E5E0D8] text-[#3B2F2F] text-xs hover:bg-[#F8F5EF] disabled:opacity-60"
                    disabled={downloading === `${v.name}:png`}
                    data-testid={`admin-monogram-download-png-${v.name}`}
                  >
                    {downloading === `${v.name}:png`
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <Download className="w-3.5 h-3.5" />}
                    PNG 2048 px
                  </button>
                  <a
                    href={v.svgUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#E5E0D8] text-[#3B2F2F] text-xs hover:bg-[#F8F5EF]"
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> Open SVG
                  </a>
                </div>

                {/* Copyable public URLs */}
                <div className="space-y-2">
                  {[
                    { kind: 'svg', label: 'SVG link', url: v.svgUrl },
                    { kind: 'png', label: 'PNG link', url: v.pngUrl },
                  ].map(({ kind, label, url }) => {
                    const key = `${v.name}:${kind}`;
                    const isCopied = copied === key;
                    return (
                      <div key={kind} className="flex items-center gap-2 bg-[#F8F5EF] rounded-lg p-2">
                        <span className="text-[10px] uppercase tracking-widest text-[#8A7A7A] shrink-0">{label}</span>
                        <code className="text-[11px] text-[#3B2F2F] truncate flex-1" title={url}>{url}</code>
                        <button
                          onClick={() => copyLink(v.name, kind, url)}
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] transition-colors ${
                            isCopied
                              ? 'bg-green-100 text-green-700'
                              : 'bg-white border border-[#E5E0D8] text-[#3B2F2F] hover:bg-[#EFE9DE]'
                          }`}
                          aria-label={`Copy ${label} for ${v.label}`}
                          data-testid={`admin-monogram-copy-${kind}-${v.name}`}
                        >
                          {isCopied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                          {isCopied ? 'Copied' : 'Copy'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Usage note */}
      <div className="bg-[#FCFBF7] border border-[#E5E0D8] rounded-xl p-5 text-sm text-[#5A4A4A]">
        <div className="font-medium text-[#3B2F2F] mb-1">Usage guide</div>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>SVG</strong> is the master file. Send it to printers and stationery designers for infinite-scale artwork (foil, letterpress, engraving, laser cut).</li>
          <li><strong>PNG 2048 px</strong> is transparent-background — drop it into digital collateral (email, social, decks).</li>
          <li>The <strong>QR Center Badge</strong> variant is used automatically inside the Wedding QR code — you do not need to swap it manually.</li>
          <li>Rebuild only if the underlying builder logic or fonts change — day-to-day use never requires it.</li>
        </ul>
      </div>
    </div>
  );
};

export default AdminMonogramLibrary;
